import {
  InputStartAdornment,
  SelectV4,
  TextInputV4,
} from '@labrav/react-components'
import { Grid, SelectChangeEvent } from '@mui/material'
import { cloneDeep, get } from 'lodash'
import {
  AzureOfferPlan,
  AzurePriceInputOption,
  CustomMeters,
  Installments,
  OfferRecurrentPricingType,
  ScheduleType,
} from '../../../../../modules/privateOffer/reducer'
import {
  Product,
  ProductPlan,
} from '../../../../../modules/productsListing/reducer'

import { FormikProps, useFormikContext } from 'formik'
import { DataType } from '@labrav/react-components/lib/@types/types/forms'
import { useStyles } from '../PlanRow/PlanRow.styles'
import { isNonEmptyNumber } from '@labrav/common'
import {
  calculateDiscountPercentage,
  getDiscountedPriceValue,
  calculateMarkupPercentage,
} from '@labrav/common'
import { getDurationValueFromBillingTerm } from '../PlanRowComponent/PlanRowComponent'
import { useCallback } from 'react'
import { getUpdatedCustomMeters } from '../PlanRow/PlanRow'
import { MsftCombinedFormState } from '../../../../../modules/microsoftCombinedForm/reducer'
import { getInstallmentsBasedOnSchedule } from '../../../../../utils/getMicrosoftInstallmentsBasedOnSchedule'
import {
  convertProductPlanPricingToOffer,
  getBillingTermsList,
  getCustomMeteres,
} from '../../../../PrivateOfferFormV2/AzureOffer/Plan/PlanRow/ContractSelection/ContractSelection'

export const ContractDetail = ({
  plan,
  product,
  index,
  selectedPaymentOptionValue,
}: {
  plan: AzureOfferPlan
  index: number
  product: Product
  selectedPaymentOptionValue: string
}) => {
  const { classes } = useStyles()
  const { values, touched, errors, handleBlur, setFieldValue } =
    useFormikContext() as FormikProps<MsftCombinedFormState>

  const productPlan = product?.plans?.find(
    planItem => planItem.planId === plan?.labraPlanId
  )

  const originalTcvValue = productPlan?.pricing?.[0]?.pricePerPaymentInUsd

  const planBillings = productPlan
    ? convertProductPlanToOfferPlan(
        productPlan
      )?.originalPricing?.recurrentPrice?.prices?.map(item =>
        getBillingTermsList(item?.billingTerm)
      )
    : []

  const handleContractDurationChange = useCallback(
    (e: SelectChangeEvent) => {
      const { value } = e.target
      if (productPlan) {
        const selectedOption = planBillings?.find(item => item.value === value)
        const billingTerm = {
          type: selectedOption?.type || '',
          value: selectedOption?.termValue || 0,
        }

        if (billingTerm) {
          const prices =
            convertProductPlanToOfferPlan(
              productPlan
            )?.originalPricing?.recurrentPrice?.prices?.filter(
              item =>
                item?.billingTerm?.type === billingTerm?.type &&
                item?.billingTerm?.value === billingTerm?.value
            ) || []

          const customMeters = getCustomMeteres({
            productPlan,
            pricesValue: prices,
          })

          const updatedCustomMeters = getUpdatedCustomMeters(
            plan,
            plan?.discountPercentage,
            { meters: customMeters } as CustomMeters
          )

          const updatedPrices = prices[0]
          const mppoDuration = getDurationValueFromBillingTerm(billingTerm)
          const installments = getInstallmentsBasedOnSchedule({
            tcv: getDiscountedPriceValue({
              price: prices[0].pricePerPaymentInUsd || 0,
              discount: plan?.discountPercentage,
            }),
            durationInMonths: mppoDuration || 0,
            paymentSchedule: ScheduleType.MONTHLY,
            now: (values?.draftOffer.offerStartDate as string) || '',
          }) as unknown as Installments[]

          const updatePlanRow = {
            ...plan,
            pricing: {
              ...plan.pricing,
              recurrentPrice: {
                ...plan.pricing.recurrentPrice,
                prices: [
                  {
                    ...updatedPrices,
                    ...(selectedPaymentOptionValue === 'flexiblePayment' &&
                    mppoDuration > 1
                      ? {
                          installments: {
                            frequency: ScheduleType.MONTHLY,
                            tcv: getDiscountedPriceValue({
                              price: prices[0].pricePerPaymentInUsd || 0,
                              discount: plan?.discountPercentage,
                            }),
                            schedule: installments,
                          },
                          pricePerPaymentInUsd: undefined,
                        }
                      : {
                          installments: undefined,
                          pricePerPaymentInUsd: getDiscountedPriceValue({
                            price: prices[0].pricePerPaymentInUsd || 0,
                            discount: plan?.discountPercentage,
                          }),
                        }),
                  },
                ],
              },
              customMeters: {
                priceInputOption: AzurePriceInputOption.USD,
                ...plan.pricing.customMeters,
                ...updatedCustomMeters,
              },
            },
          }
          const plans = [...(values.draftOffer.plans as AzureOfferPlan[])]
          plans[index] = {
            ...cloneDeep(updatePlanRow),
          } as AzureOfferPlan
          setFieldValue('draftOffer.plans', [...cloneDeep(plans)])
        }
      }
    },
    [productPlan, planBillings, selectedPaymentOptionValue, index, values, plan]
  )

  const billingTermItem = {
    ...cloneDeep(plan.pricing.recurrentPrice.prices[0].billingTerm),
  }

  const isFlexiblePayment = selectedPaymentOptionValue === 'flexiblePayment'
  const tcvValue = isFlexiblePayment
    ? plan.pricing.recurrentPrice.prices[0].installments?.tcv
    : plan.pricing.recurrentPrice.prices[0].pricePerPaymentInUsd

  const tcvKeyName = isFlexiblePayment
    ? `draftOffer.plans.${[index]}.pricing.recurrentPrice.prices.0.installments.tcv`
    : `draftOffer.plans.${[index]}.pricing.recurrentPrice.prices.0.pricePerPaymentInUsd`

  const buyerPrice = plan?.buyerPrice
  const buyerPriceKeyName = `draftOffer.plans.${[index]}.buyerPrice`

  const handleTcvChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      const numericValue = value !== '' ? Number(value) : undefined

      if (isFlexiblePayment) {
        const mppoDuration = getDurationValueFromBillingTerm(billingTermItem)
        const installments = getInstallmentsBasedOnSchedule({
          tcv: numericValue || 0,
          durationInMonths: mppoDuration || 0,
          paymentSchedule:
            plan.pricing.recurrentPrice.prices[0].installments?.frequency ||
            ScheduleType.MONTHLY,
          now: (values?.draftOffer.offerStartDate as string) || '',
        }) as unknown as Installments[]

        const updatedPlan = {
          ...plan,
          pricing: {
            ...plan.pricing,
            recurrentPrice: {
              ...plan.pricing.recurrentPrice,
              prices: [
                {
                  ...plan.pricing.recurrentPrice.prices[0],
                  installments: {
                    ...plan.pricing.recurrentPrice.prices[0].installments,
                    tcv: numericValue,
                    schedule: installments,
                  },
                },
              ],
            },
          },
          discountPercentage: calculateDiscountPercentage(
            originalTcvValue,
            numericValue
          ),
          markupPercentage: calculateMarkupPercentage(
            numericValue,
            plan?.buyerPrice
          ),
        }

        const plans = [...(values.draftOffer.plans as AzureOfferPlan[])]
        plans[index] = cloneDeep(updatedPlan)
        setFieldValue('draftOffer.plans', [...cloneDeep(plans)])
      } else {
        const updatedPlan = {
          ...plan,
          pricing: {
            ...plan.pricing,
            recurrentPrice: {
              ...plan.pricing.recurrentPrice,
              prices: [
                {
                  ...plan.pricing.recurrentPrice.prices[0],
                  pricePerPaymentInUsd: numericValue,
                },
              ],
            },
          },
          discountPercentage: calculateDiscountPercentage(
            originalTcvValue,
            numericValue
          ),
          markupPercentage: calculateMarkupPercentage(
            numericValue,
            plan?.buyerPrice
          ),
        }
        const plans = [...(values.draftOffer.plans as AzureOfferPlan[])]
        plans[index] = cloneDeep(updatedPlan)
        setFieldValue('draftOffer.plans', [...cloneDeep(plans)])
      }
    },
    [
      isFlexiblePayment,
      billingTermItem,
      plan,
      values,
      index,
      originalTcvValue,
      setFieldValue,
    ]
  )

  const handleBuyerPriceChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      const numericValue = value !== '' ? Number(value) : undefined
      const updatedPlan = {
        ...plan,
        buyerPrice: numericValue,
        markupPercentage: calculateMarkupPercentage(tcvValue, numericValue),
      }

      const plans = [...(values.draftOffer.plans as AzureOfferPlan[])]
      plans[index] = cloneDeep(updatedPlan)
      setFieldValue('draftOffer.plans', [...cloneDeep(plans)])
    },
    [plan, values, index, setFieldValue, tcvValue]
  )

  return (
    <Grid container width={'100%'} spacing={3}>
      <Grid size={4}>
        <SelectV4
          field={{
            title: 'Contract duration',
            placeHolder: 'Contract duration',
            humanReadableKey: 'plan',
            isRequired: false,
            isReadOnly: false,
            isCustom: false,
            defaultValue: '',
            dataType: 'enum' as DataType,
            options: planBillings,
          }}
          size="small"
          value={
            `${billingTermItem?.value}${' '}${billingTermItem?.type}` || ''
          }
          onChange={handleContractDurationChange}
          onBlur={handleBlur}
          isError={
            !!(
              get(
                touched,
                `draftOffer.plans.${[index]}.pricing.recurrentPrice.prices[0].billingTerm.value`,
                false
              ) &&
              get(
                errors,
                `draftOffer.plans.${[index]}.pricing.recurrentPrice.prices[0].billingTerm.value`,
                false
              )
            )
          }
          errorText={
            (!!get(
              touched,
              `draftOffer.plans.${[index]}.pricing.recurrentPrice.prices[0].billingTerm.value`,
              false
            ) &&
              get(
                errors,
                `draftOffer.plans.${[index]}.pricing.recurrentPrice.prices[0].billingTerm.value`,
                ''
              )) ||
            ''
          }
        />
      </Grid>
      <Grid size={4}>
        <div className={classes.tcv}>
          <TextInputV4
            keyName={tcvKeyName}
            title={'Discounted price (to ISV)'}
            type="number"
            placeHolder=""
            keyToPrevent={['+', '-', 'e']}
            value={isNonEmptyNumber(tcvValue) ? tcvValue! : ''}
            updatedValue={isNonEmptyNumber(tcvValue) ? tcvValue! : ''}
            startAdornment={<InputStartAdornment text="$" />}
            alignRight
            isError={
              !!(
                get(touched, tcvKeyName, false) &&
                get(errors, tcvKeyName, false)
              )
            }
            onChange={handleTcvChange}
            isReadOnly={false}
            isRequired={false}
            errorText={
              (!!get(touched, tcvKeyName, false) &&
                get(errors, tcvKeyName, '')) ||
              ''
            }
            showSpinner={false}
            onBlur={handleBlur}
            showInsideInfoIcon
            showInsideInfoIconTooltip={{
              content:
                'Enter the price offered to the ISV after applying your discount.',
            }}
          />
        </div>
      </Grid>
      <Grid size={4}>
        <TextInputV4
          keyName={`draftOffer.plans.${[index]}.buyerPrice`}
          title={'Buyer’s marked-up price'}
          type="number"
          placeHolder=""
          keyToPrevent={['+', '-', 'e']}
          value={isNonEmptyNumber(buyerPrice) ? buyerPrice! : ''}
          updatedValue={isNonEmptyNumber(buyerPrice) ? buyerPrice! : ''}
          startAdornment={<InputStartAdornment text="$" />}
          alignRight
          isError={
            !!(
              get(touched, buyerPriceKeyName, false) &&
              get(errors, buyerPriceKeyName, false)
            )
          }
          onChange={handleBuyerPriceChange}
          isReadOnly={false}
          isRequired={false}
          errorText={
            (!!get(touched, buyerPriceKeyName, false) &&
              get(errors, buyerPriceKeyName, '')) ||
            ''
          }
          showSpinner={false}
          onBlur={handleBlur}
          showInsideInfoIcon
          showInsideInfoIconTooltip={{
            content:
              'Enter the price charged to the buyer after adding your markup.',
          }}
        />
      </Grid>
    </Grid>
  )
}

export const convertProductPlanToOfferPlan = (productPlan?: ProductPlan) => {
  if (!productPlan) return productPlan

  const { pricingData, originalPricingData } =
    convertProductPlanPricingToOffer(productPlan)

  const offerPlan = {
    planId: productPlan?.planId,
    labraPlanId: productPlan.planId,
    azurePlanId: productPlan?.azurePlanId,
    description: productPlan?.description,
    displayName: productPlan?.displayName,
    offerPricingType: OfferRecurrentPricingType.SAASNEWCUSTOMIZEDPLANS,
    pricing: pricingData,
    originalPricing: originalPricingData,
    discountPercentage: 0,
    markupPercentage: 0,
    buyerPrice: pricingData?.recurrentPrice?.prices[0]?.pricePerPaymentInUsd,
  }
  return { ...offerPlan }
}
